/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */
import { useDispatch, useSelector } from 'react-redux'; // Importing hooks for Redux dispatch and selector
import {useEffect, useState, useRef} from 'react'; // Importing React hooks
import { useTranslation } from 'react-i18next';
import {useNavigate, useParams} from "react-router-dom";
import {calculateHeight, errorMsg, isSmWidth} from "../../../../Apis/functions";
import {GetMaterialRequestById} from "../../../../reduxToolkit/MaterialRequest/MaterialRequestApi";
import routes from "../../../../Routes/routes";
import {FILE_MODEL} from "../../Visitor/Visitor/Model/Model";
import {showDispatchBadge} from "../Model/Model";

const MaterialRequestDetailsApp = () => {
    const navigate = useNavigate(); // Initialize navigate function for routing
    const { t } = useTranslation(); // Translation hook to handle language translations
    const dispatch = useDispatch(); // Redux dispatch hook
    const { id } = useParams();
    const DEV = useSelector((state) => state?.CommonsSlice?.development); // Accessing development data from Redux store
    const { permission } = useSelector((state) => state.authenticatioauthennSlice); // Accessing user permissions from Redux store
    const [modified, setModified] = useState(false);
    const containerCardRef = useRef(null);
    const containerTableRef = useRef(null);
    const contentCardRef = useRef(null);
    const contentTableRef = useRef(null);
    const [item, setItem] = useState(null);
    const [showReportModal, setShowReportModal] = useState(false);
    const [evidence, setEvidence] = useState([]);
    const showBadge = showDispatchBadge(item?.dispatchStatusId);

    /**
     * Effect: Adjusts dynamic container heights based on screen size
     * and dispatch badge visibility.
     *
     * Behavior:
     * - Skips execution on small screen devices.
     * - Dynamically calculates available height for:
     *   - Main card container
     *   - Table container
     *   - Card content area
     *   - Table content area
     * - Applies different bottom spacing depending on whether
     *   the dispatch badge is visible.
     */
    useEffect(() => {
        if (isSmWidth()) return;
        const bottom = showBadge ? 80 : 70;
        calculateHeight({elementRef: containerCardRef, bottom:bottom});
        calculateHeight({elementRef: containerTableRef, bottom:bottom});
        calculateHeight({elementRef: contentCardRef, bottom:bottom});
        calculateHeight({elementRef: contentTableRef, bottom:bottom});
    }, [showBadge]);

    /**
     * Effect: Loads material request details
     * when the component is initialized.
     */
    useEffect(() => {
        getInfo();
    }, []);

    /**
     * Effect: Reloads material request information
     * after a successful modification.
     *
     * Behavior:
     * - Re-fetches request information.
     * - Resets modification state.
     */
    useEffect(() => {
        if(modified){
            getInfo();
            setModified(false);
        }
    }, [modified]); // Dependency array to trigger re-fetching on update

    /**
     * Retrieves material request detail information by identifier.
     *
     * Behavior:
     * - Fetches request information from backend service.
     * - Updates local state with:
     *   - Material request information.
     *   - Associated evidence files.
     * - Maps evidence into FILE_MODEL instances for UI handling.
     * - Displays backend error messages when request fails.
     *
     * @returns {void}
     */
    const getInfo = () => {
        dispatch(GetMaterialRequestById(id)).then(
            ({
                 payload: {
                     data: { data, success, code },
                 }
             }) => {
                if (success) {
                    setItem(data);
                    const aux = data?.evidence?.map(e => FILE_MODEL.set(e));
                    if(aux) setEvidence(aux);
                }else errorMsg(code);
            }
        );
    };

    /**
     * Navigates back to the material request main view.
     *
     * @returns {void}
     */
    const goBack = () => {
        navigate(routes.materialRequest.main)
    }

    return {
        t,
        navigate,
        DEV,
        permission,
        contentCardRef,
        contentTableRef,
        containerCardRef,
        containerTableRef,
        item,
        goBack,
        evidence,
        showBadge,
        showReportModal,
        setShowReportModal
    };
};

export default MaterialRequestDetailsApp; // Exporting the component for use in other parts of the app
